import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getInvitationByToken, acceptInvitation } from '../services/api';
import useAuth from '../hooks/useAuth';

/**
 * JoinPage - Invitation acceptance page
 *
 * URL: /join?token=abc123
 *
 * Flow:
 * 1. Load invitation details from token
 * 2. Show invite info (inviter, role, etc.)
 * 3. If user is logged in and email matches: show Accept button
 * 4. If user is not logged in: show Login button
 * 5. If email mismatch: show error
 */
export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    loadInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadInvitation = async () => {
    try {
      const res = await getInvitationByToken(token);
      if (res?.invitation) {
        setInvitation(res.invitation);
      } else {
        setError('Invitation not found');
      }
    } catch (err) {
      if (err.code === 'INVITATION_NOT_FOUND') {
        setError('This invitation link is invalid or has been revoked.');
      } else {
        setError(err.message || 'Failed to load invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);

    try {
      await acceptInvitation(token);
      // Clear stored token after successful acceptance
      sessionStorage.removeItem('inviteToken');
      // Redirect to dashboard on success
      navigate('/', { replace: true });
    } catch (err) {
      if (err.code === 'EMAIL_MISMATCH') {
        setError(`This invitation was sent to ${invitation.email}. Please log in with that email address.`);
      } else if (err.code === 'ALREADY_MEMBER') {
        setError('You are already a club member. Redirecting to dashboard...');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      } else if (err.code === 'INVITATION_EXPIRED') {
        setError('This invitation has expired. Please ask the admin to resend it.');
      } else {
        setError(err.message || 'Failed to accept invitation');
      }
    } finally {
      setAccepting(false);
    }
  };

  const getRoleBadge = (role) => {
    const labels = {
      admin: 'Admin',
      evaluator: 'Evaluator',
      speaker: 'Speaker',
      member: 'Member',
    };

    return (
      <span
        className="px-3 py-1 text-sm font-medium rounded-full"
        style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}
      >
        {labels[role] || role}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(Number(timestamp)).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Check if user's email matches the invitation
  const emailMatches = isAuthenticated && user?.email?.toLowerCase() === invitation?.email?.toLowerCase();
  const isExpired = invitation?.status === 'expired' || (invitation?.expires_at && Number(invitation.expires_at) < Date.now());
  const isAlreadyAccepted = invitation?.status === 'accepted';
  const isRevoked = invitation?.status === 'revoked';

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 mx-auto mb-4" style={{ borderColor: 'var(--border-accent)', borderTopColor: 'var(--accent)' }}></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading invitation…</p>
        </div>
      </div>
    );
  }

  // Error state (no token or invitation not found)
  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-canvas)' }}>
        <div className="max-w-md w-full rounded-2xl shadow-sm border p-8 text-center" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}>
          <p className="text-5xl mb-4">😕</p>
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>This link isn't valid.</h1>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 rounded-lg hover:opacity-90"
            style={{ background: 'var(--accent)', color: 'var(--on-primary, #fff)' }}
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-canvas)' }}>
      <div className="max-w-md w-full rounded-2xl shadow-sm border overflow-hidden" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}>
        {/* Header */}
        <div className="px-6 py-8 text-center" style={{ background: 'var(--accent)' }}>
          <span className="text-5xl">🎙️</span>
          <h1 className="text-2xl font-bold mt-4" style={{ color: 'var(--on-primary, #fff)' }}>You're invited.</h1>
          <p className="mt-2" style={{ color: 'var(--on-primary, #fff)', opacity: 0.85 }}>Join the club.</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invitation Details */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Invited by</span>
              <span className="font-medium text-right break-words min-w-0" style={{ color: 'var(--text-primary)' }}>{invitation.inviter_name}</span>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Role</span>
              {getRoleBadge(invitation.role)}
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Email</span>
              <span className="font-medium text-right break-all min-w-0" style={{ color: 'var(--text-primary)' }}>{invitation.email}</span>
            </div>

            {!isExpired && !isAlreadyAccepted && !isRevoked && (
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Expires</span>
                <span style={{ color: 'var(--text-secondary)' }}>{formatDate(invitation.expires_at)}</span>
              </div>
            )}
          </div>

          {/* Status Messages */}
          {isAlreadyAccepted && (
            <div className="rounded-lg p-4 mb-6 text-center" style={{ background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' }}>
              <p className="font-medium" style={{ color: 'var(--color-success)' }}>Already accepted. You're in.</p>
              <button
                onClick={() => navigate('/')}
                className="mt-3 text-sm hover:opacity-80"
                style={{ color: 'var(--color-success)' }}
              >
                Go to your dashboard →
              </button>
            </div>
          )}

          {isRevoked && (
            <div className="rounded-lg p-4 mb-6 text-center" style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}>
              <p className="font-medium" style={{ color: 'var(--color-danger)' }}>This invite has been revoked.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-danger)' }}>Reach out to the admin for a fresh one.</p>
            </div>
          )}

          {isExpired && !isAlreadyAccepted && (
            <div className="rounded-lg p-4 mb-6 text-center" style={{ background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)' }}>
              <p className="font-medium" style={{ color: 'var(--color-warning)' }}>This invite expired.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-warning)' }}>Ask the admin to resend it.</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-lg p-4 mb-6" style={{ background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          {invitation.status === 'pending' && !isExpired && (
            <>
              {!isAuthenticated ? (
                // Not logged in - show login prompt
                <div className="text-center">
                  <p className="mb-4" style={{ color: 'var(--text-muted)' }}>
                    Log in as <strong>{invitation.email}</strong> to accept.
                  </p>
                  <button
                    onClick={() => {
                      // Store invite token so we can return after login
                      sessionStorage.setItem('inviteToken', token);
                      // Navigate to landing page to trigger auth flow
                      window.location.href = window.location.origin + '/app/';
                    }}
                    className="w-full px-6 py-3 rounded-lg hover:opacity-90 font-medium"
                    style={{ background: 'var(--accent)', color: 'var(--on-primary, #fff)' }}
                  >
                    Sign in to accept
                  </button>
                </div>
              ) : emailMatches ? (
                // Logged in with matching email - show accept button
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full px-6 py-3 rounded-lg hover:opacity-90 font-medium disabled:opacity-50"
                  style={{ background: 'var(--accent)', color: 'var(--on-primary, #fff)' }}
                >
                  {accepting ? 'Joining…' : 'Accept and join'}
                </button>
              ) : (
                // Logged in but email doesn't match
                <div className="rounded-lg p-4 text-center" style={{ background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)' }}>
                  <p className="text-sm" style={{ color: 'var(--color-warning)' }}>
                    You're signed in as <strong>{user?.email}</strong>. This invite was sent to{' '}
                    <strong>{invitation.email}</strong>.
                  </p>
                  <p className="text-sm mt-2" style={{ color: 'var(--color-warning)' }}>
                    Sign out and sign back in with the right account.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
